/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  ShieldAlert, FlaskConical, Mail, Users, Building2, ArrowRight,
  Loader2, Trash2, RotateCcw, CheckCircle2, AlertTriangle, Layers, Beaker,
} from 'lucide-react';

const API = '/api';

const SUGGESTION_META = {
  keep_biotech: { label: 'Suggest: keep Biotech', tone: 'bg-sky-50 text-sky-700 border-sky-300' },
  keep_pharma: { label: 'Suggest: keep Pharma', tone: 'bg-emerald-50 text-emerald-700 border-emerald-300' },
  archive_both: { label: 'Suggest: archive both', tone: 'bg-rose-50 text-rose-700 border-rose-300' },
};

const ACTION_META = {
  keep_biotech: { label: 'Keep Biotech', archives: 'pharma twin', color: 'bg-sky-600 hover:bg-sky-700' },
  keep_pharma: { label: 'Keep Pharma', archives: 'biotech twin', color: 'bg-emerald-600 hover:bg-emerald-700' },
  archive_both: { label: 'Archive Both', archives: 'both records', color: 'bg-rose-600 hover:bg-rose-700' },
};


function StatCard({ icon: Icon, label, value, tone }) {
  return (
    <Card className={`border ${tone}`}>
      <CardContent className="p-4 flex items-center gap-3">
        <Icon className="w-7 h-7 opacity-60" />
        <div>
          <div className="text-2xl font-semibold leading-none">{value}</div>
          <div className="text-[11px] uppercase tracking-wide mt-1 opacity-75">{label}</div>
        </div>
      </CardContent>
    </Card>
  );
}


function PairRow({ pair, onResolve, busy }) {
  const meta = SUGGESTION_META[pair.suggestion] || SUGGESTION_META.archive_both;
  const [confirm, setConfirm] = useState(null); // {action: 'keep_biotech' | ...}

  return (
    <Card className="border-slate-200" data-testid={`dup-row-${pair.name}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Building2 className="w-4 h-4 text-slate-500 flex-shrink-0" />
              <h3 className="text-base font-semibold text-slate-800 truncate">{pair.name}</h3>
              <Badge variant="outline" className={`text-[10px] ${meta.tone}`}>{meta.label}</Badge>
            </div>
            <p className="text-xs text-slate-500 mt-1 italic">{pair.rationale}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
          {/* Biotech side */}
          <div className="rounded-md border border-sky-200 bg-sky-50/40 p-3">
            <div className="flex items-center gap-2 mb-2">
              <FlaskConical className="w-4 h-4 text-sky-700" />
              <span className="text-xs font-semibold text-sky-800 uppercase tracking-wide">Biotech</span>
              {pair.biotech.country && <span className="text-[11px] text-slate-500">· {pair.biotech.country}</span>}
            </div>
            <div className="grid grid-cols-3 gap-2 text-[11px]">
              <Stat label="Assets" value={pair.biotech.asset_count} highlight={pair.biotech.asset_count > 0 ? 'text-sky-700 font-semibold' : ''} />
              <Stat label="Emails" value={pair.biotech.emails.length} />
              <Stat label="Contacts" value={pair.biotech.contacts_count} />
            </div>
            {pair.biotech.emails.length > 0 && (
              <div className="text-[11px] text-slate-600 mt-2 truncate" title={pair.biotech.emails.join(', ')}>
                <Mail className="w-3 h-3 inline mr-1" />
                {pair.biotech.emails.slice(0, 2).join(', ')}
                {pair.biotech.emails.length > 2 ? ` +${pair.biotech.emails.length - 2}` : ''}
              </div>
            )}
            {pair.biotech.source && <div className="text-[10px] text-slate-400 mt-1">src: {pair.biotech.source}</div>}
          </div>

          {/* Pharma side */}
          <div className="rounded-md border border-emerald-200 bg-emerald-50/40 p-3">
            <div className="flex items-center gap-2 mb-2">
              <Building2 className="w-4 h-4 text-emerald-700" />
              <span className="text-xs font-semibold text-emerald-800 uppercase tracking-wide">{pair.pharma.category === 'regional_distributor' ? 'Partner' : 'Pharma'}</span>
              {pair.pharma.country && <span className="text-[11px] text-slate-500">· {pair.pharma.country}</span>}
            </div>
            <div className="grid grid-cols-3 gap-2 text-[11px]">
              <Stat label="BD records" value={pair.pharma.pharma_contact_records} highlight={pair.pharma.pharma_contact_records > 0 ? 'text-emerald-700 font-semibold' : ''} />
              <Stat label="Emails" value={pair.pharma.emails.length} />
              <Stat label="Contacts" value={pair.pharma.contacts_count} />
            </div>
            {pair.pharma.emails.length > 0 && (
              <div className="text-[11px] text-slate-600 mt-2 truncate" title={pair.pharma.emails.join(', ')}>
                <Mail className="w-3 h-3 inline mr-1" />
                {pair.pharma.emails.slice(0, 2).join(', ')}
                {pair.pharma.emails.length > 2 ? ` +${pair.pharma.emails.length - 2}` : ''}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 mt-4 flex-wrap">
          {['keep_biotech', 'keep_pharma', 'archive_both'].map(a => (
            <Button
              key={a}
              size="sm"
              disabled={busy}
              className={`text-white ${ACTION_META[a].color} ${pair.suggestion === a ? 'ring-2 ring-offset-1 ring-amber-400' : ''}`}
              onClick={() => setConfirm({ action: a })}
              data-testid={`dup-action-${a}-${pair.name}`}
              title={`Archive ${ACTION_META[a].archives}`}
            >
              {ACTION_META[a].label}
              {pair.suggestion === a && <span className="ml-1 text-[10px] opacity-90">(rec)</span>}
            </Button>
          ))}
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => onResolve(pair, 'skip')}
            data-testid={`dup-action-skip-${pair.name}`}
          >
            Skip
          </Button>
        </div>
      </CardContent>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              {confirm && ACTION_META[confirm.action].label} for "{pair.name}"?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will archive the <strong>{confirm && ACTION_META[confirm.action].archives}</strong>.
              The original record is preserved in the Archive tab and can be restored at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="dup-cancel-confirm">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { onResolve(pair, confirm.action); setConfirm(null); }}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="dup-confirm-action"
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

function Stat({ label, value, highlight = '' }) {
  return (
    <div className="text-center">
      <div className={`text-sm ${highlight}`}>{value}</div>
      <div className="text-[10px] text-slate-500 uppercase tracking-wide">{label}</div>
    </div>
  );
}


function ArchiveTab({ archived, loading, onRestore, restoring }) {
  if (loading) return <div className="py-12 text-center text-slate-500"><Loader2 className="w-6 h-6 mx-auto animate-spin mb-2" />Loading…</div>;
  if (archived.length === 0) {
    return (
      <Card className="border-dashed border-slate-300">
        <CardContent className="py-10 text-center text-slate-500">
          Nothing in the archive.
        </CardContent>
      </Card>
    );
  }
  // Group by run_id for batch restore
  const groups = {};
  archived.forEach(a => {
    const rid = a._archived?.run_id || 'unknown';
    if (!groups[rid]) groups[rid] = [];
    groups[rid].push(a);
  });
  const sortedGroups = Object.entries(groups).sort((a, b) => {
    const ta = a[1][0]._archived?.archived_at || '';
    const tb = b[1][0]._archived?.archived_at || '';
    return tb.localeCompare(ta);
  });
  return (
    <div className="space-y-4">
      {sortedGroups.map(([runId, items]) => (
        <Card key={runId} className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-xs text-slate-500">
                  Run · {new Date(items[0]._archived.archived_at).toLocaleString()}
                  <span className="text-slate-400"> · by {items[0]._archived.archived_by}</span>
                </div>
                <div className="text-sm font-medium text-slate-700 mt-0.5">
                  {items.length} record{items.length === 1 ? '' : 's'} archived
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                disabled={restoring === runId}
                onClick={() => onRestore({ run_id: runId })}
                data-testid={`archive-restore-run-${runId}`}
              >
                {restoring === runId ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RotateCcw className="w-3 h-3 mr-1" />}
                Restore all
              </Button>
            </div>
            <div className="space-y-1">
              {items.map(item => (
                <div key={`${item._archived.source_collection}-${item._archived.original_id}`} className="flex items-center justify-between text-xs py-1.5 px-2 rounded hover:bg-slate-50">
                  <div className="flex items-center gap-2 min-w-0">
                    {item._archived.source_collection === 'biotech_companies'
                      ? <FlaskConical className="w-3 h-3 text-sky-600 flex-shrink-0" />
                      : <Building2 className="w-3 h-3 text-emerald-600 flex-shrink-0" />}
                    <span className="truncate font-medium text-slate-700">{item.company_name}</span>
                    <span className="text-slate-400 text-[10px]">
                      {item._archived.source_collection === 'biotech_companies' ? 'biotech' : 'pharma'} · {item._archived.reason}
                    </span>
                  </div>
                  <Button
                    size="sm" variant="ghost"
                    className="h-6 text-[11px]"
                    disabled={restoring === item._archived.original_id}
                    onClick={() => onRestore({ original_id: item._archived.original_id, source_collection: item._archived.source_collection })}
                    data-testid={`archive-restore-one-${item._archived.original_id}`}
                  >
                    {restoring === item._archived.original_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
function AssetDedupePanel() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [logRows, setLogRows] = useState([]);

  const loadPreview = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/asset-dedupe/preview`);
      setPreview(r.data);
    } catch (e) {
      toast.error(`Asset dedup preview failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLog = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/asset-dedupe/log?limit=20`);
      setLogRows(r.data.rows || []);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { loadPreview(); loadLog(); }, [loadPreview, loadLog]);

  const runMerge = async () => {
    if (!preview) return;
    if (!window.confirm(`Merge ${preview.total_clusters} duplicate cluster(s) and delete ${preview.total_assets_to_delete} sibling row(s)? Audit trail saved to asset_merge_log.`)) return;
    setRunning(true);
    try {
      const r = await axios.post(`${API}/asset-dedupe/run`, { confirm: true });
      toast.success(`Merged ${r.data.merged_clusters} clusters · deleted ${r.data.merged_siblings_deleted} duplicates`);
      loadPreview();
      loadLog();
    } catch (e) {
      toast.error(`Merge failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setRunning(false);
    }
  };

  const total = preview?.total_clusters || 0;
  const toDelete = preview?.total_assets_to_delete || 0;
  const visibleRows = showAll ? (preview?.preview || []) : (preview?.preview || []).slice(0, 10);

  return (
    <Card className="border-violet-200 bg-gradient-to-br from-violet-50/40 to-white" data-testid="asset-dedupe-panel">
      <CardContent className="py-5 px-5 space-y-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center text-violet-700 shrink-0">
              <Layers className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-violet-900 flex items-center gap-2">
                Asset Deduplication
                {total > 0 && <Badge className="bg-violet-600 text-white text-[10px]">{total} clusters</Badge>}
              </h3>
              <p className="text-xs text-slate-600 mt-1 leading-relaxed max-w-xl">
                Merges assets that share the same biotech + same name + same indication.
                Different indications on the same name are <strong>preserved</strong> as separate campaigns.
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={loadPreview} disabled={loading}>
              <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Re-scan
            </Button>
            <Button
              size="sm"
              onClick={runMerge}
              disabled={running || total === 0}
              className="bg-violet-600 hover:bg-violet-700 text-white"
              data-testid="run-asset-merge-btn"
            >
              {running ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Beaker className="w-3.5 h-3.5 mr-1.5" />}
              {total > 0 ? `Merge ${total} clusters` : 'No duplicates'}
            </Button>
          </div>
        </div>

        {total === 0 && !loading ? (
          <div className="flex items-center gap-2 text-sm text-emerald-800 bg-emerald-50/60 border border-emerald-200 rounded p-3">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            No duplicate (biotech + name + indication) clusters detected. Asset book is clean.
          </div>
        ) : total > 0 ? (
          <div className="space-y-1.5">
            <div className="text-xs text-violet-900 mb-2">
              <strong>{total}</strong> clusters · <strong>{toDelete}</strong> sibling rows would be deleted ·
              <strong> {preview?.total_assets_after}</strong> assets remain after merge.
            </div>
            <div className="bg-white border border-violet-100 rounded-md overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-violet-50 text-violet-900 text-[10px] uppercase">
                  <tr>
                    <th className="px-3 py-2 text-left">Biotech</th>
                    <th className="px-3 py-2 text-left">Asset</th>
                    <th className="px-3 py-2 text-left">Indication</th>
                    <th className="px-3 py-2 text-center">Copies</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((p, i) => (
                    <tr key={i} className="border-t border-violet-50">
                      <td className="px-3 py-1.5 text-slate-700">{p.biotech_name || '—'}</td>
                      <td className="px-3 py-1.5 text-slate-700 font-medium">{p.primary_asset_name || p.name_norm}</td>
                      <td className="px-3 py-1.5 text-slate-500">{p.primary_indication || '—'}</td>
                      <td className="px-3 py-1.5 text-center">
                        <Badge className="bg-violet-100 text-violet-800 text-[10px]">{p.cluster_size}×</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!showAll && (preview?.preview?.length || 0) > 10 && (
              <button onClick={() => setShowAll(true)} className="text-xs text-violet-700 hover:underline mt-1">
                Show all {preview.preview.length} clusters →
              </button>
            )}
          </div>
        ) : null}

        {logRows.length > 0 && (
          <details className="text-xs">
            <summary className="cursor-pointer text-slate-600 hover:text-violet-700 select-none">
              Audit log · last {logRows.length} merge{logRows.length === 1 ? '' : 's'}
            </summary>
            <div className="mt-2 max-h-48 overflow-y-auto bg-slate-50 rounded p-2 font-mono">
              {logRows.map((l, i) => (
                <div key={i} className="text-[11px] text-slate-700 py-0.5">
                  {l.merged_at?.slice(0, 19)} · <span className="text-violet-700">{l.primary_asset_name}</span> · deleted {l.deleted_sibling_ids?.length || 0} · by {l.by_user || '—'}
                </div>
              ))}
            </div>
          </details>
        )}
      </CardContent>
    </Card>
  );
}




export default function DuplicatesInspectorPage() {
  const [tab, setTab] = useState('active');
  const [pairs, setPairs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyRow, setBusyRow] = useState(null);
  const [archived, setArchived] = useState([]);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [restoring, setRestoring] = useState(null);

  const fetchPairs = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/duplicates/companies`, { withCredentials: true });
      setPairs(data.pairs || []);
      setSummary(data.summary || null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load duplicates');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchArchived = useCallback(async () => {
    setArchiveLoading(true);
    try {
      const { data } = await axios.get(`${API}/duplicates/archived?limit=300`, { withCredentials: true });
      setArchived(data.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load archive');
    } finally {
      setArchiveLoading(false);
    }
  }, []);

  useEffect(() => { fetchPairs(); }, [fetchPairs]);
  useEffect(() => { if (tab === 'archive') fetchArchived(); }, [tab, fetchArchived]);

  const onResolve = async (pair, action) => {
    if (action === 'skip') {
      // Just hide locally
      setPairs(prev => prev.filter(p => p.name !== pair.name));
      return;
    }
    setBusyRow(pair.name);
    try {
      await axios.post(`${API}/duplicates/resolve`, {
        biotech_id: pair.biotech.id,
        pharma_id: pair.pharma.id,
        action,
      }, { withCredentials: true });
      toast.success(`${ACTION_META[action].label} — ${pair.name}`);
      setPairs(prev => prev.filter(p => p.name !== pair.name));
      // Refresh summary
      setSummary(prev => prev ? { ...prev, total: Math.max(0, prev.total - 1), [pair.suggestion]: Math.max(0, (prev[pair.suggestion] || 0) - 1) } : prev);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to resolve');
    } finally {
      setBusyRow(null);
    }
  };

  const onRestore = async (params) => {
    const key = params.run_id || params.original_id;
    setRestoring(key);
    try {
      const { data } = await axios.post(`${API}/duplicates/restore`, params, { withCredentials: true });
      toast.success(`Restored ${data.count} record(s)`);
      fetchArchived();
      fetchPairs();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Restore failed');
    } finally {
      setRestoring(null);
    }
  };

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      <header className="flex items-start justify-between gap-6 flex-wrap pb-2 border-b border-slate-200">
        <div className="flex-1 min-w-[280px]">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2">
            <ShieldAlert className="w-7 h-7 text-amber-600" />
            Duplicate Inspector
          </h1>
          <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">
            Companies tagged in both Biotech and Pharma collections. Pick a side per company —
            assets-first by default. Every action is reversible from the Archive tab.
          </p>
        </div>
        <div className="flex gap-2 flex-shrink-0 flex-wrap">
          <Button
            size="sm"
            variant={tab === 'active' ? 'default' : 'outline'}
            onClick={() => setTab('active')}
            data-testid="dup-tab-active"
          >
            Active duplicates ({summary?.total ?? '…'})
          </Button>
          <Button
            size="sm"
            variant={tab === 'archive' ? 'default' : 'outline'}
            onClick={() => setTab('archive')}
            data-testid="dup-tab-archive"
          >
            <Trash2 className="w-3 h-3 mr-1.5" /> Archive
          </Button>
        </div>
      </header>

      {tab === 'active' && (
        <>
          <AssetDedupePanel />
          {summary && summary.total > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatCard icon={ShieldAlert} label="Total dual-tagged" value={summary.total} tone="border-amber-200 bg-amber-50/30 text-amber-900" />
              <StatCard icon={FlaskConical} label="Suggest Biotech" value={summary.keep_biotech} tone="border-sky-200 bg-sky-50/30 text-sky-900" />
              <StatCard icon={Building2} label="Suggest Pharma" value={summary.keep_pharma} tone="border-emerald-200 bg-emerald-50/30 text-emerald-900" />
              <StatCard icon={Trash2} label="Suggest Archive both" value={summary.archive_both} tone="border-rose-200 bg-rose-50/30 text-rose-900" />
            </div>
          )}

          {loading ? (
            <div className="py-12 text-center text-slate-500"><Loader2 className="w-6 h-6 mx-auto animate-spin mb-2" />Scanning…</div>
          ) : pairs.length === 0 ? (
            <Card className="border-emerald-200 bg-emerald-50/40 max-w-md mx-auto">
              <CardContent className="py-8 px-6 text-center">
                <CheckCircle2 className="w-8 h-8 mx-auto text-emerald-500 mb-2" />
                <h3 className="text-sm font-semibold text-emerald-800">No duplicates 🎉</h3>
                <p className="text-xs text-emerald-700 mt-1">
                  Every company lives in exactly one collection. Re-run after the next
                  conference import.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {pairs.map(p => (
                <PairRow
                  key={`${p.biotech.id}-${p.pharma.id}`}
                  pair={p}
                  onResolve={onResolve}
                  busy={busyRow === p.name}
                />
              ))}
            </div>
          )}
        </>
      )}

      {tab === 'archive' && (
        <ArchiveTab archived={archived} loading={archiveLoading} onRestore={onRestore} restoring={restoring} />
      )}
    </div>
  );
}
